import { useRoute, useRouter } from "vue-router";
import { computed, type MaybeRefOrGetter, toValue } from "vue";
import useQueryParameter from "@/composables/query-parameter";
import { type ReadonlyRefOrGetter, useDebounce } from "@vueuse/core";
import type { SortedField } from "@/types";

export default function usePaginationParameters(
  defaults: Partial<{
    page: ReadonlyRefOrGetter<number|undefined>
    pageSize: ReadonlyRefOrGetter<number|undefined>
    filter: ReadonlyRefOrGetter<string|undefined>
    sorting: ReadonlyRefOrGetter<SortedField[]|undefined>
  }> = {}
) {
  const router = useRouter()
  const route = useRoute()

  const { asNumber: page } = useQueryParameter('page')
  const { asNumber: pageSize } = useQueryParameter('pageSize')
  const { asString: filter } = useQueryParameter('filter')
  const { asString: sorting } = useQueryParameter('sorting')

  const filterDebounced = useDebounce(filter, 500)

  const updateQuery = (params: { [k: string]: MaybeRefOrGetter<any> }) => router.push({
    query: {
      ...route.query,
      ...Object.fromEntries(
        Object.entries(params).map(
          ([k, v]) => [k, toValue(v)]
        )
      )
    }
  })

  const pageComputed = computed({
    get: () => page.value !== undefined
      ? page.value
      : toValue(defaults.page),
    set: (newValue) => updateQuery({ page: newValue === 1 ? undefined : newValue })
  })

  const pageSizeComputed = computed({
    get: () => pageSize.value !== undefined
      ? pageSize.value
      : toValue(defaults.pageSize),
    set: (newValue) => updateQuery({ pageSize: newValue, page: undefined })
  })

  const filterComputed = computed({
    get: () => filterDebounced.value !== undefined
      ? filterDebounced.value
      : toValue(defaults.filter),
    set: (newValue) => updateQuery({ filter: newValue === '' ? undefined : newValue, page: undefined })
  })

  const sortingComputed = computed({
    get: (): SortedField[] => {
      if (!sorting.value) {
        return toValue(defaults.sorting) || []
      }

      return (sorting.value?.trim() || '').split(',').map(
        field => field.length > 1
          ? { field: field.replace(/^[+-]+/, ''), direction: field[0] === '-' ? 'desc' : 'asc' }
          : undefined
      ).filter(
        (x): x is SortedField => x !== undefined
      )
    },
    set: (newValue) => updateQuery({
      sorting: newValue.length > 0
        ? newValue.map(x => `${x.direction === 'desc' ? '-' : ''}${x.field}`).join(',')
        : undefined
    })
  })

  return {
    page: pageComputed,
    pageSize: pageSizeComputed,
    filter: filterComputed,
    sorting: sortingComputed,
    // update: (
    //   params : Partial<{
    //     page: MaybeRefOrGetter<number>
    //     pageSize: MaybeRefOrGetter<number>
    //     filter: MaybeRefOrGetter<string>
    //     sorting: MaybeRefOrGetter<string>
    //   }>
    // ) => router.push({
    //   query: {
    //     ...route.query,
    //     ...Object.fromEntries(
    //       Object.entries(params).map(
    //         ([k, v]) => [k, toValue(v)]
    //       )
    //     )
    //   }
    // })
  }
}